<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import './fin/finAi.css';

// Tab Components
import GeneralTab from './tabs/GeneralTab.vue';
import ProductTab from './tabs/ProductTab.vue';
import InboxesTab from './tabs/InboxesTab.vue';
import TemplatesTab from './tabs/TemplatesTab.vue';
import DecisionLogTab from './tabs/DecisionLogTab.vue';

import TrainContentPage from './fin/pages/train/ContentPage.vue';
import TrainGuidancePage from './fin/pages/train/GuidancePage.vue';
import TrainEscalationPage from './fin/pages/train/EscalationPage.vue';
import TrainProceduresPage from './fin/pages/train/ProceduresPage.vue';
import TestWorkspacePage from './fin/pages/test/TestWorkspacePage.vue';
import DeployWorkspacePage from './fin/pages/deploy/DeployWorkspacePage.vue';
import AnalyzeWorkspacePage from './fin/pages/analyze/AnalyzeWorkspacePage.vue';
import SettingsPage from './fin/pages/settings/SettingsPage.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const assistantId = computed(() => route.params.assistantId);
const uiFlags = useMapGetter('ailabAssistants/getUIFlags');

const assistant = computed(() =>
  store.getters['ailabAssistants/getRecord'](Number(assistantId.value))
);

const assistantLoadError = ref('');
const isLoadingAssistant = ref(false);

const formatAssistantLoadError = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  'Failed to load assistant details.';

const loadAssistantRecord = async () => {
  if (!assistantId.value || assistant.value?.id || isLoadingAssistant.value) return;
  isLoadingAssistant.value = true;
  assistantLoadError.value = '';
  try {
    await store.dispatch('ailabAssistants/show', assistantId.value);
  } catch (error) {
    assistantLoadError.value = formatAssistantLoadError(error);
  } finally {
    isLoadingAssistant.value = false;
  }
};

const isFetching = computed(
  () =>
    (isLoadingAssistant.value || uiFlags.value.fetchingItem) &&
    !assistant.value?.id &&
    !assistantLoadError.value
);

const isAIGamer = computed(
  () => assistant.value?.config?.wlab_sidebar_key === 'ai_gamer'
);

const normalizeTabKey = raw => {
  const v = (raw || '').toString();
  const map = {
    // Legacy v2 tab keys -> new shell keys (keeps old deep links working)
    general: 'get_started',
    advance: 'legacy_advanced',
    policies: 'train_guidance',
    templates: 'train_escalation',
    product: 'train_content',
    preview: 'test_preview',
    simulator: 'test_preview',
    ab: 'test_preview',
    test_simulator: 'test_preview',
    test_ab: 'test_preview',
    inboxes: 'deploy_channels',
    decision_log: 'analyze_decision_log',
  };
  return map[v] || v;
};

// Active tab state
const activeTab = ref(normalizeTabKey(route.query.tab) || 'get_started');
const mainScrollRef = ref(null);

const goBack = () => {
  router.push({
    name: isAIGamer.value
      ? 'ailab_ai_gamer_index'
      : 'ailab_ai_closer_index',
  });
};

const setActiveTab = tab => {
  activeTab.value = tab;
  const nextQuery = { ...route.query, tab };
  if (tab !== 'train_content') {
    delete nextQuery.contentPanel;
  }
  router.replace({ query: nextQuery });
  if (mainScrollRef.value) {
    mainScrollRef.value.scrollTo({ top: 0, behavior: 'auto' });
  }
};

const openLegacyProductEditor = target => {
  switch (target) {
    case 'answer_templates':
      activeTab.value = 'train_content';
      router.replace({
        query: { ...route.query, tab: 'train_content', contentPanel: 'answer_templates' },
      });
      break;
    case 'links':
      activeTab.value = 'train_content';
      router.replace({
        query: { ...route.query, tab: 'train_content', contentPanel: 'links' },
      });
      break;
    case 'media':
      activeTab.value = 'train_content';
      router.replace({
        query: { ...route.query, tab: 'train_content', contentPanel: 'media' },
      });
      break;
    case 'product':
      setActiveTab('train_content');
      break;
    default:
      setActiveTab('train_content');
      break;
  }
};
const openLegacyTemplatesEditor = () => setActiveTab('legacy_templates');
const openQuickStartTab = () => setActiveTab('get_started');
const openContentPreview = () => setActiveTab('test_preview');

// Watch route query changes
watch(
  () => route.query.tab,
  newTab => {
    const normalized = normalizeTabKey(newTab);
    if (normalized && normalized !== activeTab.value) {
      activeTab.value = normalized;
      if (mainScrollRef.value) {
        mainScrollRef.value.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }
);

watch(
  () => assistantId.value,
  () => {
    loadAssistantRecord();
  },
  { immediate: true }
);

const generalRef = ref(null);
const policiesRef = ref(null);
const templatesRef = ref(null);
const contentRef = ref(null);
const proceduresRef = ref(null);
const productRef = ref(null);
const settingsRef = ref(null);

const currentSaveTarget = computed(() => {
  switch (activeTab.value) {
    case 'get_started':
      return generalRef.value;
    case 'train_guidance':
      return policiesRef.value;
    case 'train_escalation':
      return templatesRef.value;
    case 'train_content':
      return contentRef.value;
    case 'train_procedures':
      return proceduresRef.value;
    case 'legacy_product':
      return productRef.value;
    case 'settings_general':
      return settingsRef.value;
    default:
      return null;
  }
});

const canSaveCurrentTab = computed(() => {
  const target = currentSaveTarget.value;
  if (!target?.save) return false;
  if (typeof target.canSave === 'function') {
    return Boolean(target.canSave());
  }
  return true;
});

const isSavingCurrent = ref(false);
const saveCurrentTab = async () => {
  if (isSavingCurrent.value || !canSaveCurrentTab.value) return;
  isSavingCurrent.value = true;
  try {
    const result = await Promise.resolve().then(() => currentSaveTarget.value?.save?.());
    if (result !== false) {
      useAlert(t('AILAB.ASSISTANT_V2.ALERTS.SAVED_SUCCESS'));
    }
  } finally {
    isSavingCurrent.value = false;
    try {
      if (assistantId.value) {
        await store.dispatch('aiCloser/fetchConfig', {
          assistantId: assistantId.value,
        });
      }
    } catch (e) {
      // Best-effort; UI already showed per-tab alerts.
    }
  }
};

onMounted(() => {
  loadAssistantRecord();
});
</script>

<template>
  <div class="ai-shell flex flex-col w-full h-full overflow-hidden">
    <header class="ai-shell__header flex items-center justify-between flex-shrink-0">
      <div class="ai-shell__header-main flex items-center gap-3">
        <button
          type="button"
          class="ai-shell__back-btn"
          aria-label="Back"
          @click="goBack"
        >
          <span class="i-ph-caret-left-bold size-4" />
        </button>
        <div class="flex flex-col min-w-0">
          <div
            class="text-base font-semibold text-slate-900 truncate max-w-[520px]"
          >
            {{
              assistant?.name ||
              t('AILAB.ASSISTANT_V2.HEADER.LOADING_ASSISTANT')
            }}
          </div>
          <div class="text-xs text-slate-500 truncate max-w-[520px]">
            {{ t('AILAB.ASSISTANT_V2.HEADER.SUBTITLE') }}
          </div>
        </div>
      </div>

      <div class="ai-shell__header-actions flex items-center gap-2">
        <Button
          size="sm"
          icon="i-lucide-save"
          :label="t('AILAB.FORM.SAVE')"
          :is-loading="isSavingCurrent"
          :disabled="isSavingCurrent || !canSaveCurrentTab"
          @click="saveCurrentTab"
        />
      </div>
    </header>

    <div class="flex flex-1 min-h-0 overflow-hidden">
      <main class="ai-shell__main flex-1 min-h-0 overflow-hidden">
        <div
          v-if="isFetching"
          class="flex items-center justify-center h-full text-slate-500"
        >
          <Spinner />
        </div>

        <div
          v-else-if="assistantLoadError && !assistant?.id"
          class="h-full flex items-center justify-center px-6"
        >
          <div class="ai-shell__error-card">
            <div class="ai-shell__error-title">Unable to load this assistant</div>
            <div class="ai-shell__error-copy">
              {{ assistantLoadError }}
            </div>
            <div class="ai-shell__error-actions">
              <button type="button" class="btn o__primary" @click="loadAssistantRecord">
                Retry
              </button>
              <button type="button" class="btn o__secondary" @click="goBack">
                Back to list
              </button>
            </div>
          </div>
        </div>

        <div
          ref="mainScrollRef"
          v-else
          class="ai-shell__scroll h-full min-h-0 overflow-y-auto"
          :class="{
            'ai-shell__scroll--workspace': [
              'get_started',
              'train_content',
              'train_guidance',
              'train_escalation',
              'train_procedures',
              'test_preview',
              'deploy_channels',
              'analyze_decision_log',
              'settings_general',
            ].includes(activeTab),
          }"
        >
          <!-- Get Started -->
          <GeneralTab
            v-if="activeTab === 'get_started'"
            ref="generalRef"
            :assistant="assistant"
          />

          <!-- Train -->
          <TrainContentPage
            v-if="activeTab === 'train_content'"
            ref="contentRef"
            :assistant="assistant"
            @open-legacy="openLegacyProductEditor"
            @open-preview="openContentPreview"
          />

          <TrainGuidancePage
            v-if="activeTab === 'train_guidance'"
            ref="policiesRef"
            :assistant="assistant"
            :is-saving-all="isSavingCurrent"
          />

          <TrainEscalationPage
            v-if="activeTab === 'train_escalation'"
            ref="templatesRef"
            :assistant="assistant"
            :is-saving-all="isSavingCurrent"
            @open-legacy="openLegacyTemplatesEditor"
          />
          <TrainProceduresPage
            v-if="activeTab === 'train_procedures'"
            ref="proceduresRef"
            :assistant="assistant"
            @open-quick-start="openQuickStartTab"
            @open-legacy-product="openLegacyProductEditor"
          />
          <TemplatesTab
            v-if="activeTab === 'legacy_templates' && isAIGamer"
            :assistant="assistant"
            :is-saving-all="isSavingCurrent"
          />
          <div v-else-if="activeTab === 'legacy_templates'" class="p-6">
            <div class="text-lg font-semibold text-slate-900">
              Legacy templates page retired
            </div>
            <div class="text-sm text-slate-600 mt-1">
              Non-AIGamer assistants no longer use a separate Templates editor.
              Use Train &gt; Escalation for runtime fallback and risk routing, or
              Settings for remaining compatibility fields.
            </div>
            <div class="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('train_escalation')"
              >
                Open Escalation
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('settings_general')"
              >
                Open Settings
              </button>
            </div>
          </div>

          <div v-if="activeTab === 'legacy_advanced'" class="p-6">
            <div class="text-lg font-semibold text-slate-900">
              Advanced compatibility page
            </div>
            <div class="text-sm text-slate-600 mt-1">
              Legacy Advanced is no longer a primary editor. Use Get Started for Quick
              Start, Train for runtime behavior, and Settings for compatibility JSON.
            </div>
            <div class="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('get_started')"
              >
                Open Get Started
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('train_guidance')"
              >
                Open Guidance
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('train_procedures')"
              >
                Open Procedures
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('settings_general')"
              >
                Open Settings
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="openLegacyProductEditor('product')"
              >
                Open Legacy Product
              </button>
            </div>
          </div>

          <div v-if="activeTab === 'legacy_product'" class="p-6">
            <div class="text-lg font-semibold text-slate-900">
              Legacy product editor split into focused compatibility pages
            </div>
            <div class="text-sm text-slate-600 mt-1">
              Product basics, media, links, and answer templates no longer need to
              open inside the same legacy page. Use the focused compatibility routes
              below while Train &gt; Content continues its owner migration.
            </div>
            <div class="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="openLegacyProductEditor('product')"
              >
                Open Product Basics
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="openLegacyProductEditor('media')"
              >
                Open Media Library
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="openLegacyProductEditor('answer_templates')"
              >
                Open Answer Templates
              </button>
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="openLegacyProductEditor('links')"
              >
                Open Website Sync
              </button>
            </div>
          </div>
          <ProductTab
            v-if="activeTab === 'legacy_product_basics'"
            ref="productRef"
            :assistant="assistant"
            :visible-tabs="['information']"
            initial-tab="information"
            title-override="Legacy product basics"
            description-override="Compatibility-only editor for product records that have not been migrated into Train > Content yet."
          />
          <div v-if="activeTab === 'legacy_answer_templates'" class="p-6">
            <div class="text-lg font-semibold text-slate-900">
              Legacy answer templates page retired
            </div>
            <div class="text-sm text-slate-600 mt-1">
              Legacy snippets are now managed inside Train &gt; Content. This route
              remains only for compatibility with old deep links.
            </div>
            <div class="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('train_content')"
              >
                Open Train Content
              </button>
            </div>
          </div>
          <div v-if="activeTab === 'legacy_links'" class="p-6">
            <div class="text-lg font-semibold text-slate-900">
              Legacy website sync page retired
            </div>
            <div class="text-sm text-slate-600 mt-1">
              Website sync is now managed inside Train &gt; Content. This route remains
              only for compatibility with old deep links.
            </div>
            <div class="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('train_content')"
              >
                Open Train Content
              </button>
            </div>
          </div>
          <div v-if="activeTab === 'legacy_media'" class="p-6">
            <div class="text-lg font-semibold text-slate-900">
              Legacy media page retired
            </div>
            <div class="text-sm text-slate-600 mt-1">
              Uploaded documents are now managed inside Train &gt; Content. This route
              remains only for compatibility with old deep links.
            </div>
            <div class="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                class="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                @click="setActiveTab('train_content')"
              >
                Open Train Content
              </button>
            </div>
          </div>

          <!-- Test -->
          <TestWorkspacePage
            v-if="activeTab === 'test_preview'"
            :assistant="assistant"
            initial-view="preview"
          />

          <!-- Deploy -->
          <DeployWorkspacePage
            v-if="activeTab === 'deploy_channels'"
            :assistant="assistant"
          />
          <InboxesTab
            v-if="activeTab === 'legacy_inboxes'"
            :assistant="assistant"
          />

          <!-- Analyze -->
          <AnalyzeWorkspacePage
            v-if="activeTab === 'analyze_decision_log'"
            :assistant="assistant"
          />
          <DecisionLogTab
            v-if="activeTab === 'legacy_decision_log'"
            :assistant="assistant"
          />

          <!-- Settings (migrated) -->
          <SettingsPage
            v-if="activeTab === 'settings_general'"
            ref="settingsRef"
            :assistant="assistant"
          />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
/* Main Container - Card Layout */
.assistant-v2-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: #000000;
}

:global(body:not(.dark) .assistant-v2-page) {
  background: #f1f5f9;
}

.page-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 6px;
  overflow: hidden;
}

/* Header Card */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px;
  background: #1c2026;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0;
  z-index: 10;
}

:global(body:not(.dark) .page-header) {
  background: var(--wlab-bg-tertiary);
}

:global(body.dark .page-header) {
  background: #1c2026;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #a8aaae;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
  color: #e5e7eb;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #e5e7eb;
  margin: 0;
}

:global(body:not(.dark) .header-title) {
  color: var(--wlab-text-primary);
}

.header-subtitle {
  font-size: 13px;
  color: #6b6f76;
  margin: 0;
}

:global(body:not(.dark) .header-subtitle) {
  color: var(--wlab-text-muted);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.save-btn-header {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  color: #000000;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

:global(body:not(.dark) .save-btn-header) {
  background: #0066ff;
  color: #ffffff;
}

:global(body.dark .save-btn-header) {
  background: #ffffff;
  color: #000000;
}

.save-btn-header:hover:not(:disabled) {
  background: #e5e7eb;
}

:global(body:not(.dark) .save-btn-header:hover:not(:disabled)) {
  background: linear-gradient(135deg, #3388ff 0%, #0066ff 100%);
}

:global(body.dark .save-btn-header:hover:not(:disabled)) {
  background: #e5e7eb;
}

.save-btn-header:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Main Container - Card Layout */
.main-container {
  display: flex;
  flex: 1;
  gap: 6px;
  overflow: hidden;
  z-index: 10;
  min-height: 0;
}

/* Sidebar Card */
.sidebar {
  width: 240px;
  background: #1c2026;
  border-radius: 12px 12px 0 12px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
}

:global(body:not(.dark) .sidebar) {
  background: var(--wlab-bg-tertiary);
}

:global(body.dark .sidebar) {
  background: #1c2026;
}

.sidebar-content {
  padding: 24px 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.section-spacing {
  margin-top: 32px;
}

.section-title {
  padding: 0 12px 8px;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

:global(body:not(.dark) .section-title) {
  color: var(--wlab-text-muted);
}

:global(body.dark .section-title) {
  color: #ffffff;
}

.nav-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.nav-item.active {
  background: rgba(255, 255, 255, 0.15);
}

:global(body:not(.dark) .nav-item.active) {
  background: rgba(0, 102, 255, 0.1);
}

:global(body.dark .nav-item.active) {
  background: rgba(255, 255, 255, 0.15);
}

.nav-icon {
  width: 18px;
  height: 18px;
  color: #6b6f76;
  flex-shrink: 0;
}

.nav-item.active .nav-icon {
  color: #ffffff;
}

:global(body:not(.dark) .nav-item.active .nav-icon) {
  color: #0066ff;
}

:global(body.dark .nav-item.active .nav-icon) {
  color: #ffffff;
}

.nav-item:hover .nav-icon {
  color: #a8aaae;
}

.nav-label {
  font-size: 14px;
  font-weight: 500;
  color: #a8aaae;
}

.nav-item.active .nav-label {
  color: #e5e7eb;
  font-weight: 600;
}

:global(body:not(.dark) .nav-item.active .nav-label) {
  color: var(--wlab-text-primary);
}

:global(body.dark .nav-item.active .nav-label) {
  color: #e5e7eb;
}

.nav-item:hover .nav-label {
  color: #e5e7eb;
}

:global(body:not(.dark) .nav-item:hover .nav-label) {
  color: var(--wlab-text-primary);
}

/* Content Area Card */
.content-area {
  flex: 1;
  overflow-y: auto;
  position: relative;
  min-height: 0;
  background: #1c2026;
  border-radius: 12px 12px 12px 0;
}

:global(body:not(.dark) .content-area) {
  background: var(--wlab-bg-tertiary);
}

:global(body.dark .content-area) {
  background: #1c2026;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.content-wrapper {
  padding: 32px 40px;
  padding-bottom: 60px;
  position: relative;
  min-height: 100%;
  max-width: 1200px;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Phase A shell styles (Inbox-settings-like sidebar)                          */
/* ────────────────────────────────────────────────────────────────────────── */

.ai-shell__sidebar {
  width: 264px;
  flex: 0 0 264px;
  border-right: 1px solid #eeeeef;
}

.ai-shell__sidebar-inner {
  padding: 16px 14px 20px;
}

.ai-shell__nav-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.ai-shell__nav-item {
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 10px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  text-align: left;
}

.ai-shell__nav-item:hover {
  background: #f1f5f9;
}

.ai-shell__nav-item--active {
  background: #eef2ff;
  color: #111827;
}

.ai-shell__back-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #334155;
}

.ai-shell__back-btn:hover {
  background: #f8fafc;
}

.ai-shell {
  background: #f5f7fb;
}

.ai-shell__header {
  height: 64px;
  padding: 0 28px;
  border-bottom: 1px solid #e5eaf2;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
}

.ai-shell__header-main {
  min-width: 0;
}

.ai-shell__header-actions {
  padding-left: 12px;
}

.ai-shell__main {
  background: #f5f7fb;
}

.ai-shell__scroll {
  background: #f5f7fb;
}

.ai-shell__scroll--workspace {
  padding: 20px 20px 20px;
}

.ai-shell__error-card {
  width: min(480px, 100%);
  border: 1px solid #fecdd3;
  border-radius: 18px;
  background: #fff7f7;
  padding: 24px;
  box-shadow: var(--fin-shadow-0);
}

.ai-shell__error-title {
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  color: #9f1239;
}

.ai-shell__error-copy {
  margin-top: 8px;
  font-size: 14px;
  line-height: 21px;
  color: #475569;
}

.ai-shell__error-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}
</style>
